import { gridLayerConfig } from "@/constants/layerConfigAttributes";
import { FeatureHubGuard } from "@/featureFlags";
import type { LayerUnion } from "@/layers";
import { IntegerInput } from "@/overlay/components/CommonParts/CustomInput";
import type { DraggableItemIdentifier } from "@/overlay/components/CommonParts/SortableList";
import CompleteThreshold from "@/overlay/components/CommonParts/Threshold/CompleteThreshold";
import { type ThresholdColor, transformColorThresholds } from "@/overlay/components/CommonParts/Threshold/types";
import { InitDateField } from "@/overlay/components/LayerStackWindow/InitDateField";
import {
  CalibratedToggle,
  ColorPickerInput,
  OpacityInput,
  StepConfigInput,
} from "@/overlay/components/LayerStackWindow/LayerFormElements";
import { ModelAndWeatherParameterForm } from "@/overlay/components/ModelAndWeatherParameterForm";
import { FormField } from "@/overlay/components/form-field";
import { batchGroupBy } from "@/reducer/batchGroupBy";
import type { MapId } from "@/types/type-utils";
import { hasCalibratedAvailable } from "@/utility/calibratedAvailabilityMap";
import { t } from "@lingui/core/macro";
import type { GridLayer } from "@mm/metx-workbench.meteomatics.com";

export function GridLayerUpdateForm(props: {
  layer: GridLayer;
  mapId: MapId;
  changeProps: (propsValue: Partial<LayerUnion>) => void;
  onEditLayerParam: () => void;
}) {
  const { layer, mapId, changeProps, onEditLayerParam } = props;
  const thresholds = transformColorThresholds(layer.thresholds);

  function changeGridProps(propsValue: Partial<GridLayer>) {
    changeProps(propsValue as Partial<LayerUnion>);
  }

  function moveThresholdIdx(dragStart: DraggableItemIdentifier, dropTarget: DraggableItemIdentifier) {
    if (!thresholds.length) {
      return;
    }

    const copyOfThresholds = [...thresholds];
    const fromIdx = dragStart.index;

    let toIdx = dropTarget.index;
    if (toIdx >= copyOfThresholds.length) {
      toIdx = copyOfThresholds.length - 1;
    }
    if (toIdx < 0) {
      toIdx = 0;
    }

    const targetThreshold = copyOfThresholds.splice(fromIdx, 1)[0];
    copyOfThresholds.splice(toIdx, 0, targetThreshold);

    changeGridProps({ thresholds: copyOfThresholds });
  }

  return (
    <>
      <ModelAndWeatherParameterForm layer={layer} mapId={mapId} onEditParameter={onEditLayerParam} />
      <OpacityInput
        layer={layer}
        change={changeProps}
        onStart={() => batchGroupBy.start()}
        onEnd={() => batchGroupBy.end()}
      />
      <ColorPickerInput
        label={t`Text Color`}
        change={(color) => changeGridProps({ text_color: color })}
        value={layer.text_color}
      />
      <FormField label={t`Text Size`}>
        <IntegerInput
          {...gridLayerConfig.textSizeLimits}
          onChange={(value) => changeGridProps({ text_size: value })}
          value={layer.text_size || 0}
        />
      </FormField>
      <StepConfigInput layer={layer} stepRange={{ ...gridLayerConfig.stepRange }} id={mapId} change={changeProps} />
      {hasCalibratedAvailable(layer.parameter_unit) && (
        <CalibratedToggle value={!!layer.calibrated} change={changeProps} />
      )}
      <FeatureHubGuard flag="api_url_suffix">
        <InitDateField
          label={t`API URL suffix`}
          defaultValue={(layer.custom_options as { api_url_suffix?: string } | undefined)?.api_url_suffix ?? ""}
          onChange={(apiUrlSuffix) =>
            changeGridProps({
              custom_options: {
                ...(layer.custom_options ?? {}),
                api_url_suffix: apiUrlSuffix,
              },
            })
          }
        />
      </FeatureHubGuard>
      <CompleteThreshold
        onMoveIndex={moveThresholdIdx}
        thresholds={thresholds}
        advancedThresholds={[]}
        updateThresholds={(newThresholds: ThresholdColor[]) => {
          changeGridProps({ thresholds: newThresholds });
        }}
        updateAdvancedThresholds={() => {
          // Grid layers currently support basic thresholds only.
        }}
      />
    </>
  );
}
